import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useMutation } from '@apollo/client';
import get from 'lodash/get';

import {
  GENERATE_UPLOAD_REVIEW_IMAGE,
  REMOVE_REVIEW_IMAGE,
} from 'components/Comments/CommentQueries';

import fetchWithThrow from 'services/fetchWithThrow';

const useHandleImageUpload = () => {
  const [uploadingError, setUploadingError] = useState(null);
  const [reviewImage, setReviewImage] = useState(null);
  const [removedExistingImage, setRemovedExistingImage] = useState(false);
  const [uploadInProgress, setUploadInProgress] = useState(false);

  const handleRemoveExistingImage = () => {
    setRemovedExistingImage(true);
  };

  const [generateUploadUrl, { loading: generateURLInProgress }] = useMutation(
    GENERATE_UPLOAD_REVIEW_IMAGE,
    {
      onError: err => {
        console.error('Failed to generate image URL', {
          graphQLErrors: err.graphQLErrors[0]?.extensions?.code,
          networkError: err.networkError,
        });

        const errorCode = get(err, 'graphQLErrors[0].extensions.code');
        const niceErrorCode = errorCode ? ` (code: ${errorCode})` : '';
        const niceUploadError =
          get(err, 'graphQLErrors[0].message') ||
          `We can't generate upload url for this image right now. Please try again later ${niceErrorCode}`;

        setUploadingError(niceUploadError);

        // Log the error to the error logging service
        if (window.sendError) {
          window.sendError({
            error: {
              code: errorCode || 'GENERATE_UPLOAD_URL_ERROR',
              message: `Failed to generate upload URL: ${niceUploadError}`,
              name: 'ImageUploadError',
              stack: err.stack || new Error().stack,
            },
          });
        }
      },
    },
  );

  const [deleteImage, { loading: deleteInProgress }] = useMutation(REMOVE_REVIEW_IMAGE, {
    onCompleted() {
      console.info('DELETE OP COMPLETED!');
    },
    onError: err => {
      console.error('Failed to delete image', {
        graphQLErrors: err.graphQLErrors[0]?.extensions?.code,
        networkError: err.networkError,
      });

      const errorCode = get(err, 'graphQLErrors[0].extensions.code');
      const niceErrorCode = errorCode ? ` (code: ${errorCode})` : '';
      const niceUploadError =
        get(err, 'graphQLErrors[0].message') ||
        `We can't delete this image right now. Please try again later ${niceErrorCode}`;

      setUploadingError(niceUploadError);

      // Log the error to the error logging service
      if (window.sendError) {
        window.sendError({
          error: {
            code: errorCode || 'DELETE_IMAGE_ERROR',
            message: `Failed to delete image: ${niceUploadError}`,
            name: 'ImageDeleteError',
            stack: err.stack || new Error().stack,
          },
        });
      }
    },
  });

  const isHandlingImageUpload = useMemo(
    () => deleteInProgress || uploadInProgress || generateURLInProgress,
    [deleteInProgress, uploadInProgress, generateURLInProgress],
  );

  const callUploadImage = async (contentId, userId = undefined) => {
    try {
      const { data } = await generateUploadUrl({
        variables: {
          content_id: contentId,
          fileType: reviewImage.type,
          user_id: userId,
        },
      });

      const signedUrl = data.generateUploadUrl;

      setUploadInProgress(true);
      await fetchWithThrow(signedUrl, {
        method: 'PUT',
        headers: { 'Content-Type': reviewImage.type },
        body: reviewImage,
      });
      setUploadInProgress(false);
      toast.success('Image uploaded successfully!');
    } catch (e) {
      console.error('[ERROR] reviewImage = ', e);
      setUploadInProgress(false);

      toast.error('Failed to upload image! You can try again by editing your comment.');

      // Log the error to the error logging service
      if (window.sendError) {
        window.sendError({
          error: {
            code: 'IMAGE_UPLOAD_ERROR',
            message: `Failed to upload review image: ${e.message || 'Unknown error'}`,
            name: 'ImageUploadError',
            stack: e.stack || new Error().stack,
          },
        });
      }
    }
  };

  const callDeleteImage = async (contentId, userId = undefined) => {
    try {
      const { data } = await deleteImage({
        variables: {
          content_id: contentId,
          user_id: userId,
        },
      });

      const responseDelete = data.removeReviewImage;

      if (!responseDelete) {
        console.error('err delete = ', responseDelete);
        toast.error('Failed to delete image! You can try again by editing your comment.');

        // Log the error to the error logging service
        if (window.sendError) {
          window.sendError({
            error: {
              code: 'IMAGE_DELETE_ERROR',
              message: 'Failed to delete review image: API returned false',
              name: 'ImageDeleteError',
              stack: new Error().stack,
            },
          });
        }
      }
    } catch (e) {
      console.error('err delete = ', e);
      toast.error('Failed to delete image! You can try again by editing your comment.');

      // Log the error to the error logging service
      if (window.sendError) {
        window.sendError({
          error: {
            code: 'IMAGE_DELETE_ERROR',
            message: `Failed to delete review image: ${e.message || 'Unknown error'}`,
            name: 'ImageDeleteError',
            stack: e.stack || new Error().stack,
          },
        });
      }
    }
  };

  return {
    isHandlingImageUpload,
    callUploadImage,
    callDeleteImage,
    uploadingError,
    setReviewImage,
    removedExistingImage,
    handleRemoveExistingImage,
    reviewImage,
    setUploadingError,
  };
};

export default useHandleImageUpload;
